import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Zap, BarChart2, MessageCircle, Layout,
  MapPin, CreditCard, FileText, ChevronDown, ChevronUp, Star,
  Clock, TrendingUp, AlertTriangle, Package, Shirt, Wrench,
  Camera, Scissors, Printer, Menu, X, Calculator, Download, Bot, Copy, ExternalLink, BookOpen, PlayCircle
} from 'lucide-react';
import SEO from '../components/SEO';

// ─── Utility ──────────────────────────────────────────────────────────────────
const formatRp = (n) => 'Rp ' + n.toLocaleString('id-ID');

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: 'Cara Kerja', href: '#cara-kerja' },
    { label: 'Fitur', href: '#fitur' },
    { label: 'Harga', href: '#harga' },
    { label: 'Tutorial', href: '#tutorial' },
    { label: 'Testimoni', href: '#testimoni' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#0f6c44] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white font-extrabold text-sm font-manrope">K</span>
          </div>
          <span className="font-extrabold text-slate-900 text-lg font-manrope tracking-tight">KasirWA</span>
        </div>

        <div className="hidden md:flex items-center gap-6">
          {links.map(l => (
            <a key={l.label} href={l.href}
              className="text-sm font-medium text-slate-600 hover:text-[#0f6c44] transition-colors">
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={() => navigate('/login')}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-[#0f6c44] transition-colors">
            Masuk
          </button>
          <button onClick={() => navigate('/register')}
            className="px-5 py-2 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            Daftar Gratis
          </button>
        </div>

        <button className="md:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors" onClick={() => setMenuOpen(v => !v)}>
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-5 py-4 space-y-3">
          {links.map(l => (
            <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}
              className="block text-sm font-medium text-slate-700 py-2 hover:text-[#0f6c44]">
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-2 border-t border-slate-100">
            <button onClick={() => navigate('/login')} className="flex-1 h-10 border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors">Masuk</button>
            <button onClick={() => navigate('/register')} className="flex-1 h-10 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] transition-colors">Daftar Gratis</button>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const navigate = useNavigate();
  const requestPwaInstall = () => window.dispatchEvent(new CustomEvent('kasirwa:request-pwa-install'));
  const [videoStarted, setVideoStarted] = useState(false);
  return (
    <section className="pt-28 pb-20 px-5 bg-gradient-to-b from-emerald-50/60 to-white">
      <div className="max-w-6xl mx-auto">
        {/* Announcement banner */}
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-[#0f6c44] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
          <span className="w-2 h-2 bg-[#0f6c44] rounded-full animate-pulse" />
        Solusi Aplikasi Kasir & Billing Untuk Bisnis Jasa Nomor 1 di Indonesia
          <ArrowRight size={13} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 font-manrope leading-[1.1] tracking-tight">
              Nota, Tracking, Reminder <br/> 
              <span className="text-[#0f6c44]">Otomatis Lewat WhatsApp</span> 
            </h1>
            <p className="text-slate-600 text-base sm:text-lg mt-5 leading-relaxed max-w-xl">
              Sistem kasir digital untuk UMKM jasa — <strong className="text-slate-800 font-semibold">Laundry, Servis Elektronik, Bengkel, Rental</strong>, dan sejenisnya. Nota otomatis tercetak, pelanggan dapat <span className="text-[#0f6c44] font-semibold">reminder &amp; update status lewat WhatsApp</span>, tanpa Anda harus repot balas chat satu per satu.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => navigate('/register')}
                className="flex items-center gap-2 px-6 py-3.5 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-md shadow-emerald-200">
                Daftar Gratis Khusus untuk Usaha Anda <ArrowRight size={18} />
              </button>
             
            </div>

            <div className="flex items-center gap-2 mt-4">
              <CheckCircle size={15} className="text-[#0f6c44]" />
              <span className="text-sm text-slate-500">Pelanggan Gak Khawatir</span>
              <span className="text-slate-200 mx-1">·</span>
              <CheckCircle size={15} className="text-[#0f6c44]" />
              <span className="text-sm text-slate-500">Owner Gak Pusing</span>
            </div>
          </div>

         

          <div className="w-full">
            <div className="aspect-video w-full overflow-hidden rounded-2xl border border-emerald-100 bg-slate-900 shadow-[0_16px_44px_rgba(15,108,68,0.16)]">
              {videoStarted ? <iframe data-testid="landing-intro-video" className="h-full w-full" src="https://www.youtube-nocookie.com/embed/XkZaPczy2xA?autoplay=1&playsinline=1&controls=1&rel=0&iv_load_policy=3" title="KasirWA — Kasir Laundry Digital dalam 3 Menit" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" /> : <button onClick={() => setVideoStarted(true)} data-testid="play-landing-intro-video-button" aria-label="Putar video perkenalan KasirWA" className="group relative flex h-full w-full items-center justify-center overflow-hidden"><img src="https://i.ytimg.com/vi/XkZaPczy2xA/maxresdefault.jpg" alt="Video perkenalan KasirWA" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"/><span className="absolute inset-0 bg-slate-950/25 transition-colors group-hover:bg-slate-950/15"/><span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white/95 pl-1 text-[#0f6c44] shadow-xl transition-transform group-hover:scale-110"><span className="h-0 w-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-[#0f6c44]"/></span></button>}
            </div>
            <p className="mt-4 text-center font-manrope text-base font-bold text-slate-700">Lihat Bagaimana KasirWA dapat membantu Usaha Anda</p>
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const navigate = useNavigate();
  const steps = [
    { n: '1', icon: FileText, title: 'Buat Pesanan', desc: 'Isi nama pelanggan, jenis layanan, dan estimasi selesai. Cukup 30 detik.' },
    { n: '2', icon: MessageCircle, title: 'Kirim Nota WA', desc: '1 klik kirim nota + link tracking langsung ke WhatsApp pelanggan.' },
    { n: '3', icon: MapPin, title: 'Pelanggan Lacak Sendiri', desc: 'Pelanggan buka link tracking dari WA — lihat status, tagihan, tanpa chat ke toko.' },
    { n: '4', icon: Zap, title: 'Pengingat Otomatis Terkirim', desc: 'Saat selesai, sistem kirim notifikasi WA otomatis — pelanggan langsung ambil & bayar.' },
  ];
  return (
    <section id="cara-kerja" className="py-20 px-5 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">CARA KERJANYA</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Mulai dalam 5 Menit,<br />Tanpa Ribet</h2>
          <p className="text-slate-500 mt-3 max-w-md mx-auto">Aplikasi KasirWA bisa langsung pakai untuk Transaksi.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
          {/* connector line */}
          <div className="hidden lg:block absolute top-10 left-[calc(12.5%+1rem)] right-[calc(12.5%+1rem)] h-0.5 bg-emerald-100 z-0" />
          {steps.map((s, i) => (
            <div key={i} className="relative z-10 bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#0f6c44] rounded-xl flex items-center justify-center flex-shrink-0">
                  <s.icon size={18} className="text-white" />
                </div>
                <span className="text-2xl font-extrabold text-slate-200 font-manrope">{s.n}</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2 font-manrope">{s.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button onClick={() => navigate('/register')}
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-md shadow-emerald-200">
            Daftar Gratis <ArrowRight size={18} />
          </button>
          <p className="text-xs text-slate-400 mt-2">Gratis 1.000 transaksi pertama · Setup cepat dalam 2 menit tanpa kartu kredit.</p>
        </div>
      </div>
    </section>
  );
}

// ─── Pain Points ──────────────────────────────────────────────────────────────
function PainPoints() {
  const items = [
    { icon: Clock, color: 'bg-red-50 text-red-600', title: '15 Jam/Minggu Habis untuk CS', desc: 'Kasir kelelahan membalas chat status yang sama berulang-ulang: "Sudah diproses belum kak?". Dampak: Pelayanan lambat & kasir burnout.' },
    { icon: CreditCard, color: 'bg-orange-50 text-orange-600', title: 'Kas Macet & Piutang Menumpuk', desc: 'Barang yang sudah selesai ngendon berminggu-minggu di rak karena toko lupa/malas mem-follow up tagihan pelunasan. Dampak: Perputaran arus kas toko tersendat.' },
    { icon: AlertTriangle, color: 'bg-yellow-50 text-yellow-600', title: 'Sengketa Kerusakan Barang', desc: 'Usaha Servis, seringkali Pelanggan menuduh barang lecet/luntur/cacat di toko karena tidak ada bukti foto kondisi fisik saat pertama diserahkan. Dampak: Toko terpaksa ganti rugi uang.' },
    { icon: FileText, color: 'bg-slate-50 text-slate-600', title: 'Nota Kertas Hilang & Berantakan', desc: 'Kertas nota tercecer atau hilang dibawa pelanggan, kasir kesulitan mencari barang di antara tumpukan ratusan order. Dampak: Operasional kacau & reputasi turun.' },
  ];
  return (
    <section className="py-20 px-5 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-red-500 uppercase tracking-widest">THE HIDDEN COST</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Pemilik Usaha Jasa, Inikah Masalah yang Sering Kamu Alami?</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((item, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className={`w-11 h-11 ${item.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4`}>
                <item.icon size={20} className={item.color.split(' ')[1]} />
              </div>
              <h3 className="font-bold text-slate-900 mb-2 font-manrope">{item.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Business Verticals ───────────────────────────────────────────────────────
function BusinessVerticals() {
  const [active, setActive] = useState(0);
  const verticals = [
    {
      icon: Shirt, label: 'Laundry',
      subtitle: 'Kiloan · Sepatu · Helm · Karpet · Boneka',
      quote: '"Kak cuciannya sudah selesai belum?" — Pertanyaan ini tidak akan datang lagi karena pelanggan sudah dapat pengingat otomatis lebih dulu.',
      features: [
        { title: 'Cucian Selesai', desc: 'Pelanggan dapat WhatsApp otomatis begitu laundry siap diambil, lengkap dengan total tagihan.' },
        { title: 'Cucian Ngendon', desc: 'Pengingat otomatis dikirim bila cucian belum diambil setelah 2–3 hari supaya rak tidak penuh.' },
        { title: 'Nota Digital', desc: 'Kirim rincian item, berat, dan biaya langsung ke WA pelanggan — tanpa nota kertas yang hilang.' },
      ],
    },
    {
      icon: Camera, label: 'Rental',
      subtitle: 'Mobil · Motor · Kamera · Tenda · Sound System',
      quote: 'Penyewa sering lupa atau pura-pura lupa jam kembali. Dengan pengingat otomatis, kamu tidak perlu kejar-kejaran telepon.',
      features: [
        { title: 'H-1 Jam Habis Sewa', desc: 'Penyewa dapat pengingat otomatis bahwa masa sewanya hampir berakhir — mau perpanjang atau segera kembali?' },
        { title: 'Sewa Overdue', desc: 'Jika aset belum dikembalikan setelah jam jatuh tempo, sistem langsung mengirim peringatan lunak ke penyewa.' },
        { title: 'Checklist Kondisi Aset', desc: 'Foto & catatan kondisi unit sebelum diserahkan — terlindungi dari klaim kerusakan palsu.' },
      ],
    },
    {
      icon: Wrench, label: 'Servis Gadget',
      subtitle: 'HP · Laptop · TV · Konsol · Home Appliance',
      quote: 'Pelanggan titip HP dan tidak tahu kabarnya 5 hari. Lalu komplain padahal barangnya sudah jadi dari kemarin.',
      features: [
        { title: 'HP/Laptop Selesai Servis', desc: 'Pelanggan dapat notifikasi WhatsApp otomatis bahwa barangnya sudah bisa diambil.' },
        { title: 'Konfirmasi Perkiraan Biaya', desc: 'Sebelum dikerjakan, pelanggan bisa setujui perkiraan biaya langsung dari WhatsApp — tanpa harus telepon.' },
        { title: 'Bukti Kondisi Awal', desc: 'Foto cacat fisik dicatat saat barang masuk — toko terlindungi dari tuduhan kerusakan.' },
      ],
    },
    {
      icon: Wrench, label: 'Bengkel',
      subtitle: 'Motor · Mobil · Cuci & Salon · Detailing',
      quote: 'Motor sudah kelar tapi pemiliknya malah minta dijemput ke rumah karena tidak tahu bengkelnya sudah selesai dari tadi.',
      features: [
        { title: 'Kendaraan Siap Diambil', desc: 'Notifikasi otomatis ke pemilik kendaraan — lengkap dengan rincian ganti oli dan sparepart yang dikerjakan.' },
        { title: 'Jadwal Servis Berikutnya', desc: 'Ingatkan pelanggan untuk servis berkala berikutnya — cara mudah untuk mendatangkan pelanggan balik.' },
        { title: 'Riwayat Kendaraan', desc: 'Semua catatan plat nomor, kilometer, dan ganti sparepart tersimpan rapi per kendaraan.' },
      ],
    },
    {
      icon: Scissors, label: 'Penjahit',
      subtitle: 'Permak · Jas · Kebaya · Seragam · Custom Baju',
      quote: 'Baju pengantin sudah jadi dari seminggu lalu tapi tidak ada yang tahu. Pelanggan datang mendadak H-2 nikah sambil panik.',
      features: [
        { title: 'Baju Selesai Dijahit', desc: 'Pemberitahuan langsung ke WhatsApp pelanggan bahwa baju sudah bisa diambil atau di-fitting.' },
        { title: 'Pengingat Jadwal Fitting', desc: 'Kirim otomatis H-1 sebelum jadwal fitting agar pelanggan tidak lupa datang.' },
        { title: 'Tagihan Sisa Pelunasan', desc: 'Pengingat lunak dikirim ke pelanggan yang masih punya sisa DP saat baju sudah jadi.' },
      ],
    },
    {
      icon: Printer, label: 'Percetakan',
      subtitle: 'Banner · Sablon · Souvenir · Katering Aqiqah',
      quote: 'Banner 50 pcs sudah selesai cetak tapi pelanggannya entah ke mana — barang menumpuk di meja produksi selama 3 hari.',
      features: [
        { title: 'Pesanan Siap Diambil', desc: 'Begitu produksi selesai, pelanggan langsung dapat notifikasi WhatsApp untuk segera mengambil pesanannya.' },
        { title: 'Update Tahapan Produksi', desc: 'Pelanggan bisa pantau sendiri apakah pesanannya sedang di tahap desain, cetak, atau finishing.' },
        { title: 'Pengingat Pelunasan', desc: 'Kirim tagihan sisa DP via WhatsApp sebelum pesanan diambil — tidak perlu tagih manual yang canggung.' },
      ],
    },
  ];
  const v = verticals[active];
  return (
    <section className="py-20 px-5 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">COCOK UNTUK BISNIS APA SAJA?</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Dirancang Khusus untuk Bisnis<br />yang Terima &amp; Kerjakan Pesanan</h2>
          <p className="text-slate-500 mt-3 max-w-lg mx-auto text-sm">Setiap jenis usaha punya cara kerja yang berbeda. KasirWA hadir dengan fitur pengingat WhatsApp yang relevan untuk masing-masing bisnismu.</p>
        </div>
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {verticals.map((vrt, i) => (
            <button key={i} onClick={() => setActive(i)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${active === i ? 'bg-[#0f6c44] text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              <vrt.icon size={16} />{vrt.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#0f6c44] rounded-xl flex items-center justify-center flex-shrink-0">
                <v.icon size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 font-manrope">{v.label} &amp; Perawatan</h3>
                <p className="text-xs text-slate-500">{v.subtitle}</p>
              </div>
            </div>
            <p className="text-sm text-slate-500 italic border-l-2 border-[#0f6c44] pl-4 my-5 leading-relaxed">{v.quote}</p>
          </div>
          <div className="space-y-4">
            {v.features.map((f, i) => (
              <div key={i} className="flex gap-4 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle size={16} className="text-[#0f6c44]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-0.5">{f.title}</p>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Before / After ───────────────────────────────────────────────────────────
function BeforeAfter() {
  const before = [
    'Pelanggan tanya status via WA — kasir harus cek nota kertas satu per satu',
    'Lupa follow-up pelanggan yang belum ambil — rak & gudang penuh, kas macet',
    'Nota kertas hilang atau basah — sengketa barang rusak tidak bisa dibuktikan',
    'Laporan omset dihitung manual dari tumpukan struk setiap akhir bulan',
    'Jam sewa habis tapi tidak ada yang mengingatkan — aset terlambat kembali',
  ];
  const after = [
    'Pelanggan cek status dari link WA sendiri — kasir tidak lagi diganggu pertanyaan status',
    'Pengingat otomatis terkirim saat selesai & jika belum diambil — kas berputar lebih cepat',
    'Foto kondisi barang & nota digital tersimpan — terlindungi dari klaim kerusakan palsu',
    'Laporan omset, laba, dan piutang tersedia realtime — tidak perlu hitung manual',
    'Pengingat H-1 jam otomatis ke penyewa — aset kembali tepat waktu tanpa kejar-kejaran',
  ];
  return (
    <section className="py-20 px-5 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">TRANSFORMASI NYATA</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Toko yang Sama, Cara Kerja yang Berbeda</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-red-100 p-7 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
                <X size={16} className="text-red-500" />
              </div>
              <span className="font-bold text-slate-700 font-manrope">SEBELUM — Cara Lama</span>
            </div>
            <ul className="space-y-3.5">
              {before.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-600">
                  <div className="w-5 h-5 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X size={11} className="text-red-400" />
                  </div>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl border border-emerald-200 p-7 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle size={16} className="text-[#0f6c44]" />
              </div>
              <span className="font-bold text-[#0f6c44] font-manrope">SESUDAH — Pakai KasirWA</span>
            </div>
            <ul className="space-y-3.5">
              {after.map((a, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-700">
                  <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle size={11} className="text-[#0f6c44]" />
                  </div>
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── App Preview ──────────────────────────────────────────────────────────────
function AppPreview() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const screens = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart2,
      url: 'https://kasirwa.com/admin/dashboard',
      image: '/preview/dashboard.png',
      alt: 'Tampilan Dashboard KasirWA',
      tag: 'Ringkasan Bisnis',
      title: 'Pantau Omset, Kas & Piutang Real-time',
      desc: 'Lihat performa pendapatan hari ini, kas diterima, sisa piutang yang belum lunas, dan grafik tren mingguan dalam satu layar bersih.'
    },
    {
      id: 'kanban',
      label: 'Alur Status Pesanan',
      icon: Layout,
      url: 'https://kasirwa.com/admin/kanban',
      image: '/preview/kanban-board.png',
      alt: 'Tampilan Alur Status Pesanan KasirWA',
      tag: 'Alur Kerja Visual',
      title: 'Kelola Status Pesanan Semudah Drag & Drop',
      desc: 'Pesanan terbagi jelas antara Masuk, Diproses, Siap Diambil, hingga Selesai. Karyawan dan kasir langsung tahu prioritas kerjaan.'
    },
    {
      id: 'order-detail',
      label: 'Detail Pesanan',
      icon: FileText,
      url: 'https://kasirwa.com/admin/orders/M8KJVUQDG',
      image: '/preview/detail-pesanan.png',
      alt: 'Tampilan Detail Pesanan KasirWA',
      tag: 'Manajemen Order & Struk',
      title: 'Rincian Lengkap, Cetak Struk & Riwayat WA',
      desc: 'Status pembayaran, rincian biaya, tombol kirim WA otomatis, hingga pilihan download nota PDF atau cetak printer thermal Bluetooth.'
    },
    {
      id: 'tracking',
      label: 'Lacak Pesanan (Pelanggan)',
      icon: MapPin,
      url: 'https://kasirwa.com/track/AXMKD2Q1W',
      image: '/preview/lacak-pesanan.png',
      alt: 'Tampilan Halaman Lacak Pesanan Pelanggan',
      tag: 'Self-Service Pelanggan',
      title: 'Pelanggan Pantau Status dari Link WhatsApp',
      desc: 'Pelanggan bisa melihat progress cucian/servis secara mandiri, melihat sisa tagihan, info rekening transfer, dan konfirmasi pembayaran.'
    },
    {
      id: 'laporan',
      label: 'Laporan Laba Rugi',
      icon: TrendingUp,
      url: 'https://kasirwa.com/admin/reports/profit-loss',
      image: '/preview/laporan.png',
      alt: 'Tampilan Laporan Keuangan KasirWA',
      tag: 'Analisis Keuangan',
      title: 'Laba Bersih & Margin Per Produk Akurat',
      desc: 'Hitung total omset, modal pokok (HPP), biaya operasional, hingga margin keuntungan per jenis layanan secara instan dan bisa diekspor.'
    },
  ];

  const current = screens[tab];

  return (
    <section className="py-20 px-5 bg-gradient-to-b from-white via-slate-50/50 to-white overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest bg-emerald-50 px-3.5 py-1 rounded-full border border-emerald-200/70">
            LIHAT TAMPILAN APLIKASI
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">
            Sederhana, Cepat &amp; Bisa Langsung Dipakai
          </h2>
          <p className="text-slate-500 mt-3 max-w-lg mx-auto text-sm sm:text-base">
            Tidak butuh training. Kasir baru pun langsung paham dalam 5 menit pertama.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-8">
          {screens.map((s, i) => {
            const Icon = s.icon;
            const isActive = tab === i;
            return (
              <button
                key={s.id}
                onClick={() => setTab(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-[#0f6c44] text-white shadow-md shadow-emerald-700/20 scale-[1.02]'
                    : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/80 shadow-sm'
                }`}
              >
                <Icon size={16} className={isActive ? 'text-white' : 'text-[#0f6c44]'} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Browser Frame Mockup */}
        <div className="relative max-w-5xl mx-auto">
          {/* Ambient Glow */}
          <div className="absolute -inset-1.5 bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-emerald-600/15 rounded-[28px] blur-xl opacity-80 -z-10" />

          <div className="bg-slate-900/90 rounded-[22px] p-2 sm:p-3 shadow-[0_20px_50px_-15px_rgba(15,108,68,0.2)] border border-slate-800">
            {/* Browser Window Chrome */}
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200/80 shadow-inner flex flex-col">
              {/* Browser Header Bar */}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 border-b border-slate-200">
                {/* Traffic lights */}
                <div className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 rounded-full bg-[#ff5f56] border border-[#e0443e] shadow-sm flex items-center justify-center cursor-pointer group">
                    <span className="opacity-0 group-hover:opacity-100 text-[8px] text-[#5c0000] font-bold">✕</span>
                  </div>
                  <div className="w-3.5 h-3.5 rounded-full bg-[#ffbd2e] border border-[#dea123] shadow-sm flex items-center justify-center cursor-pointer group">
                    <span className="opacity-0 group-hover:opacity-100 text-[8px] text-[#5c4000] font-bold">−</span>
                  </div>
                  <div className="w-3.5 h-3.5 rounded-full bg-[#27c93f] border border-[#1aab29] shadow-sm flex items-center justify-center cursor-pointer group">
                    <span className="opacity-0 group-hover:opacity-100 text-[8px] text-[#004a0e] font-bold">+</span>
                  </div>
                </div>

                {/* URL Bar */}
                <div className="flex-1 max-w-md mx-3 sm:mx-6">
                  <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-1.5 text-xs text-slate-600 font-mono border border-slate-200 shadow-sm">
                    <span className="flex items-center gap-1 text-emerald-700 font-sans text-[10px] font-bold bg-emerald-100/70 px-1.5 py-0.5 rounded">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                      SSL
                    </span>
                    <span className="text-slate-400">https://</span>
                    <span className="text-slate-800 font-medium truncate">{current.url.replace('https://', '')}</span>
                  </div>
                </div>

                {/* Right controls placeholder */}
                <div className="flex items-center gap-1.5 text-slate-300">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                </div>
              </div>

              {/* Screenshot Image Container */}
              <div className="relative bg-slate-900/5 flex items-center justify-center overflow-hidden min-h-[320px] sm:min-h-[460px]">
                <img
                  key={current.id}
                  src={current.image}
                  alt={current.alt}
                  className="w-full h-auto max-h-[580px] object-contain object-top transition-opacity duration-300 ease-in-out select-none"
                  loading="lazy"
                />
              </div>

              {/* Bottom Feature Description Bar */}
              <div className="bg-slate-50/80 border-t border-slate-100 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-bold text-[#0f6c44] uppercase tracking-wider bg-emerald-100/70 px-2 py-0.5 rounded-md">
                      {current.tag}
                    </span>
                    <h4 className="text-sm sm:text-base font-bold text-slate-800 font-manrope">
                      {current.title}
                    </h4>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 max-w-2xl leading-relaxed">
                    {current.desc}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm shadow-emerald-200"
                >
                  Daftar Gratis <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Coba Langsung ───────────────────────────────────────────────────────────
function CobaLangsung() {
  const navigate = useNavigate();
  const [active, setActive] = useState('aplikasi');
  const copy = async value => { try { await navigator.clipboard.writeText(value); } catch {} };
  const items = [
    { id: 'aplikasi', icon: Layout, label: 'Coba Aplikasi', title: 'Masuk ke KasirWA dengan data contoh', text: 'Lihat Dashboard, Kanban, Inventory, CRM, biaya operasional, dan Laba Rugi dengan data contoh yang selalu rapi.', image: '/coba-langsung/dashboard-owner.png' },
    { id: 'lacak', icon: MapPin, label: 'Lacak Pesanan', title: 'Cek status pesanan pelanggan melalui Whatsapp', text: 'Lihat detail pesanan, status pengerjaan, total tagihan, dan halaman lacak yang dikirim ke WhatsApp pelanggan.', image: '/coba-langsung/asisten-toko.png' },
    { id: 'asisten', icon: Bot, label: 'Asisten Toko', title: 'Pantau Usaha Anda melalui Whatsapp', text: 'Kirim keyword Hari ini, Profit, Piutang untuk menerima informasi status langsung dari asisten KasirWA.', image: '/coba-langsung/lacak-pesanan.png' },
  ];
  const current = items.find(item => item.id === active);
  return <section className="bg-white px-5 py-20"><div className="mx-auto max-w-6xl"><div className="mb-10 text-center"><span className="rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1 text-xs font-bold uppercase tracking-widest text-[#0f6c44]">COBA LANGSUNG</span><h2 className="mt-3 font-manrope text-3xl font-extrabold text-slate-900 sm:text-4xl">Rasakan Alur KasirWA<br/>Sebelum Membuat Toko Anda</h2><p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">Telusuri tampilan aplikasi, halaman pelanggan, dan asisten WhatsApp dengan data contoh yang direset setiap hari.</p></div>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.8fr_1.2fr]"><div className="space-y-2">{items.map(item=>{const Icon=item.icon;const selected=active===item.id;return <button key={item.id} onClick={()=>setActive(item.id)} data-testid={`live-try-${item.id}-tab`} className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-[transform,background-color,border-color] hover:-translate-y-0.5 ${selected?'border-[#0f6c44] bg-emerald-50 text-[#0f6c44]':'border-slate-100 bg-white text-slate-600 hover:bg-slate-50'}`}><span className={`flex h-10 w-10 items-center justify-center rounded-xl ${selected?'bg-[#0f6c44] text-white':'bg-slate-100 text-slate-500'}`}><Icon size={19}/></span><span><span className="block text-sm font-bold">{item.label}</span><span className="mt-0.5 block text-xs text-slate-400">{item.title}</span></span></button>})}<div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"><p className="text-sm font-bold text-amber-900">Mode Coba</p><p className="mt-1 text-xs leading-relaxed text-amber-800">Data contoh direset setiap hari agar tetap rapi untuk pengunjung berikutnya.</p></div></div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-[0_16px_40px_rgba(15,23,42,0.08)]"><div className="border-b border-slate-100 bg-white px-5 py-4"><h3 className="font-manrope text-lg font-bold text-slate-900">{current.title}</h3><p className="mt-1 text-sm leading-relaxed text-slate-500">{current.text}</p></div><div className="bg-slate-100 p-3 sm:p-5"><img src={current.image} alt={current.title} data-testid="live-try-preview-image" className="max-h-[480px] w-full rounded-xl object-contain object-top shadow-sm"/></div><div className="flex flex-wrap gap-3 bg-white p-5">{active==='aplikasi'&&<><button onClick={()=>copy('coba.owner@kasirwa.com')} data-testid="copy-demo-owner-email-button" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Copy size={14}/>Owner: coba.owner@kasirwa.com</button><button onClick={()=>copy('coba.staff@kasirwa.com')} data-testid="copy-demo-staff-email-button" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Copy size={14}/>Staff: coba.staff@kasirwa.com</button><button onClick={()=>copy('CobaKasirWA2026!')} data-testid="copy-demo-password-button" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Copy size={14}/>Password: CobaKasirWA2026!</button><button onClick={()=>navigate('/login')} data-testid="open-live-try-login-button" className="inline-flex items-center gap-2 rounded-xl bg-[#0f6c44] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c5636]">Masuk & Coba <ArrowRight size={14}/></button></>}{active==='lacak'&&<a href="/track/NLZTZZG2Z" target="_blank" rel="noreferrer" data-testid="open-live-tracking-button" className="inline-flex items-center gap-2 rounded-xl bg-[#0f6c44] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0c5636]">Lacak Transaksi<ExternalLink size={15}/></a>}{active==='asisten'&&<a href="https://wa.me/6289518293051?text=Hari%20Ini" target="_blank" rel="noreferrer" data-testid="open-live-chatbot-button" className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1ebe57]"><MessageCircle size={16}/>Cek Penjualan/OMSET/Piutang Hari Ini</a>}</div></div></div></div></section>;
}


// ─── Features ─────────────────────────────────────────────────────────────────
function Features() {
  const features = [
    { icon: MapPin, title: 'Lacak Pesanan', desc: 'Pelanggan bisa cek progres pengerjaan dan sisa tagihan kapan saja dari WhatsApp tanpa perlu menelepon toko.' },
    { icon: Layout, title: 'Visual Antrean Pesanan', desc: 'Semua pesanan yang masuk, sedang dikerjakan, dan siap diambil tertata rapi dalam layar visual yang mudah dipahami karyawan.' },
    { icon: Package, title: 'Pengambilan Barang Cepat', desc: 'Nomor lemari / rak tercatat di nota. Kasir bisa mengambil barang titipan pelanggan hanya dalam hitungan detik.' },
    { icon: CreditCard, title: 'Pengingat Tagihan', desc: 'Kelola uang muka (DP), tagihan tempo kantor, dan kirim pesan pengingat tagihan ke pelanggan dalam sekali klik.' },
    { icon: BarChart2, title: 'Laporan Keuangan dan Keuntungan', desc: 'Ketahui omset kotor, modal biaya bahan, laba bersih nyata, serta laporan kinerja harian kasir dan karyawan.' },
    { icon: FileText, title: 'Dokumentasi Lengkap', desc: 'Simpan foto kondisi barang saat pertama diserahkan untuk melindungi toko dari risiko komplain dan tuduhan ganti rugi.' },
  ];
  return (
    <section id="fitur" className="py-20 px-5 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">KEUNGGULAN BISNIS</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Dirancang untuk Menambah Keuntungan<br />&amp; Mempermudah Kerja Kasir</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <f.icon size={20} className="text-[#0f6c44]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2 font-manrope">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ROI Calculator ───────────────────────────────────────────────────────────
function ROICalculator() {
  const [txPerDay, setTxPerDay] = useState(25);
  const csHoursPerMonth = Math.round(txPerDay * 0.05 * 30);
  const daysToCollect = Math.max(0.5, 3 - txPerDay * 0.04).toFixed(1);
  return (
    <section className="py-20 px-5 bg-slate-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">KALKULATOR PENGHEMATAN</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Hitung Berapa Jam &amp; Uang<br />yang Bisa Anda Selamatkan</h2>
        </div>
        <div className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-sm">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <label className="font-semibold text-slate-800 font-manrope">Rata-rata Transaksi Masuk per Hari</label>
              <span className="text-2xl font-extrabold text-[#0f6c44] font-manrope">{txPerDay}</span>
            </div>
            <input type="range" min={5} max={100} value={txPerDay} onChange={e => setTxPerDay(+e.target.value)}
              className="w-full accent-[#0f6c44] h-2 rounded-full cursor-pointer" />
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>5</span><span>100 Transaksi / Hari</span></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Clock size={22} className="text-[#0f6c44]" />
              </div>
              <p className="text-4xl font-extrabold text-slate-900 font-manrope">{csHoursPerMonth}</p>
              <p className="font-semibold text-slate-700 mt-1">Jam</p>
              <p className="text-sm text-slate-500 mt-0.5">Per Bulan Bebas Tanya Status</p>
              <p className="text-xs text-[#0f6c44] font-medium mt-2">Waktu CS yang Dihemat</p>
            </div>
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <TrendingUp size={22} className="text-[#0f6c44]" />
              </div>
              <p className="text-4xl font-extrabold text-slate-900 font-manrope">{daysToCollect}</p>
              <p className="font-semibold text-slate-700 mt-1">Hari</p>
              <p className="text-sm text-slate-500 mt-0.5">Lebih Cepat Diambil &amp; Dilunasi</p>
              <p className="text-xs text-[#0f6c44] font-medium mt-2">Pencairan Kas Piutang</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────
function Pricing() {
  const navigate = useNavigate();
  const plans = [
    {
      name: 'Starter',
      tagline: 'Gratis Selamanya',
      price: 'Rp 0',
      period: '1.000 Pesanan Pertama',
      highlight: false,
      badge: null,
      features: [
        'Gratis hingga 1.000 transaksi pertama',
        'Kirim nota digital langsung ke WhatsApp pelanggan',
        'Pelanggan bisa lacak status pengerjaan & unduh nota',
        '1 Akun Pengelola Toko',
      ],
      cta: 'Mulai Pakai Gratis',
    },
    {
      name: 'Standard',
      tagline: 'Kirim Pesan 1-Klik',
      price: 'Rp 50.000',
      period: '/ bulan',
      highlight: false,
      badge: null,
      features: [
        'Jumlah Transaksi & Pesanan Tanpa Batas',
        'Pengingat 1-Klik: Membuka aplikasi WhatsApp kasir',
        '1 Akun Pengelola Toko',
        'Cocok untuk Laundry, Rental Mobil, Servis HP & Salon',
        'Laporan Omset, Laba Bersih & Pengingat Piutang',
      ],
      cta: 'Pilih Paket Rp 50rb',
    },
    {
      name: 'Pro Automation',
      tagline: 'Gratis 3 Bulan — Otomatis WhatsApp API',
      price: 'Gratis',
      period: '3 bulan pertama',
      highlight: true,
      badge: 'Promo',
      features: [
        'Pengingat WhatsApp Otomatis Tanpa Klik Manual',
        'Bebas Sambungkan Nomor WhatsApp Toko',
        'Multi-Akun Karyawan & Kasir Tanpa Batas',
        'Hak Akses Terpisah: Pemilik Toko & Kasir Operasional',
        'Fitur Buka/Tutup Kasir & Catat Biaya Operasional Toko',
        'Laporan Ringkasan Harian Otomatis ke WhatsApp Pemilik Toko',
      ],
      cta: 'Claim Gratis 3 Bulan',
    },
  ];
  return (
    <section id="harga" className="py-20 px-5 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-4">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">PILIHAN PAKET LANGGANAN</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Investasi Fleksibel Sesuai<br />Kebutuhan Bisnis Anda</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500 mb-10">
          {['Tidak perlu kartu kredit', 'Tidak ada kontrak jangka panjang', 'Batalkan langganan kapan saja', 'Setup & aktif dalam 5 menit'].map(t => (
            <span key={t} className="flex items-center gap-1.5"><CheckCircle size={13} className="text-[#0f6c44]" />{t}</span>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map(p => (
            <div key={p.name}
              className={`rounded-2xl border p-7 flex flex-col relative ${p.highlight ? 'bg-[#0f6c44] border-[#0c5636] shadow-xl shadow-emerald-200' : 'bg-white border-slate-100 shadow-sm'}`}>
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-[#0f6c44] text-xs font-extrabold px-4 py-1 rounded-full border border-emerald-200 shadow-sm whitespace-nowrap">
                  {p.badge}
                </div>
              )}
              <div className="mb-5">
                <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${p.highlight ? 'text-emerald-200' : 'text-slate-400'}`}>{p.name}</p>
                <p className={`font-bold text-lg font-manrope ${p.highlight ? 'text-white' : 'text-slate-900'}`}>{p.tagline}</p>
              </div>
              <div className="mb-6">
                <span className={`text-4xl font-extrabold font-manrope ${p.highlight ? 'text-white' : 'text-slate-900'}`}>{p.price}</span>
                <span className={`text-sm ml-1 ${p.highlight ? 'text-emerald-200' : 'text-slate-400'}`}>{p.period}</span>
                {p.highlight && <p className="text-emerald-200 text-xs mt-1">Selanjutnya Rp. 100.000/Bulan</p>}
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className={`flex gap-2.5 text-sm ${p.highlight ? 'text-emerald-50' : 'text-slate-600'}`}>
                    <CheckCircle size={15} className={`flex-shrink-0 mt-0.5 ${p.highlight ? 'text-emerald-300' : 'text-[#0f6c44]'}`} />
                    <span dangerouslySetInnerHTML={{ __html: f.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/register')} data-testid={`pricing-register-${p.name.toLowerCase().replaceAll(' ', '-')}`}
                className={`w-full h-12 rounded-xl font-semibold active:scale-95 transition-all ${p.highlight ? 'bg-white text-[#0f6c44] hover:bg-emerald-50' : 'bg-[#0f6c44] text-white hover:bg-[#0c5636]'}`}>
                {p.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Tutorial & Direct Help ───────────────────────────────────────────────────
const LANDING_TUTORIALS = [
  { id: 'setting-toko', label: 'Setting Toko', title: 'Setting Toko — Tonton', description: 'Atur profil toko, kategori usaha, nomor WhatsApp, dan metode pembayaran.', videoId: 'AhxgCymokA0' },
  { id: 'manajemen-produk', label: 'Manajemen Produk', title: 'Manajemen Produk — Tonton', description: 'Tambahkan layanan atau produk, kategori, harga jual, HPP, dan stok.', videoId: 'IqGnsvIdbEk' },
  { id: 'transaksi-kasir', label: 'Transaksi & Kasir', title: 'Transaksi & Kasir — Tonton', description: 'Buat pesanan, catat pembayaran, kelola progres, lalu cetak nota.', videoId: 'IdC8zzVGDhA' },
  { id: 'crm-whatsapp', label: 'CRM & WhatsApp', title: 'CRM & WhatsApp — Tonton', description: 'Kelola pelanggan, segmentasi, riwayat komunikasi, dan notifikasi toko.', videoId: 'gOD4g2E7RUI' },
  { id: 'biaya-laporan', label: 'Biaya & Laporan', title: 'Biaya & Laporan — Tonton', description: 'Pahami biaya operasional, kas, piutang, serta laba rugi usaha.', videoId: 'yIt_KnWCZVs' },
  { id: 'whatsapp-otomatis', label: 'WhatsApp Otomatis', title: 'WhatsApp Otomatis — Tonton', description: 'Gunakan notifikasi otomatis serta chatbot pelanggan dan owner toko.', videoId: 'IfDPQvhBulY' },
];

function TutorialSection() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(LANDING_TUTORIALS[0].id);
  const active = LANDING_TUTORIALS.find(item => item.id === activeId) || LANDING_TUTORIALS[0];
  return <section id="tutorial" className="bg-white px-5 py-20"><div className="mx-auto max-w-6xl">
    <div className="mx-auto mb-12 max-w-3xl text-center"><span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3.5 py-1 text-xs font-bold uppercase tracking-widest text-[#0f6c44]"><BookOpen size={14}/>Panduan Video</span><h2 className="mt-4 font-manrope text-3xl font-extrabold text-slate-900 sm:text-4xl">Mulai Digitalisi Bisnis Anda dalam Hitungan Menit!</h2><p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">Tidak perlu untuk jadi Ahli IT. KasirWA dirancang sesederhana mungkin agar siapa pun—termasuk Anda yang merasa &apos;gaptek&apos;—bisa langsung mengelola toko dengan profesional.</p></div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[270px_minmax(0,1fr)]"><nav aria-label="Daftar video tutorial" className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible">{LANDING_TUTORIALS.map((item, index) => { const selected = active.id === item.id; return <button key={item.id} onClick={() => setActiveId(item.id)} data-testid={`landing-tutorial-tab-${item.id}`} aria-selected={selected} className={`min-w-[200px] rounded-xl border p-3 text-left transition-[transform,background-color,border-color] hover:-translate-y-0.5 lg:min-w-0 ${selected ? 'border-[#0f6c44] bg-emerald-50' : 'border-slate-100 bg-white hover:bg-slate-50'}`}><span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${selected ? 'bg-[#0f6c44] text-white' : 'bg-slate-100 text-slate-500'}`}>{index + 1}</span><span className={`mt-2 block text-sm font-bold ${selected ? 'text-[#0f6c44]' : 'text-slate-700'}`}>{item.label}</span></button>; })}</nav>
      <div data-testid="landing-active-tutorial" className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_28px_rgba(15,23,42,0.08)]"><div className="border-b border-slate-100 p-5 sm:p-6"><div className="flex items-start gap-3"><span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-50 text-[#0f6c44]"><PlayCircle size={20}/></span><div><p className="text-xs font-bold uppercase tracking-widest text-[#0f6c44]">Tutorial {LANDING_TUTORIALS.findIndex(item => item.id === active.id) + 1} dari {LANDING_TUTORIALS.length}</p><h3 className="mt-1 font-manrope text-xl font-bold text-slate-900">{active.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-500">{active.description}</p></div></div></div><div className="aspect-video bg-slate-950"><iframe key={active.videoId} data-testid="landing-active-tutorial-video" className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${active.videoId}?controls=1&rel=0&playsinline=1`} title={active.title} loading="lazy" allow="fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin"/></div></div>
    </div>
    <div className="mt-10 text-center"><button onClick={() => navigate('/register')} data-testid="landing-tutorial-register-button" className="inline-flex items-center gap-2 rounded-xl bg-[#0f6c44] px-7 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-200 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#0c5636]"><span>Daftar Sekarang</span><ArrowRight size={17}/></button><p className="mt-3 text-xs text-slate-500">Mulai kelola toko Anda dengan alur yang sudah Anda lihat.</p></div>
  </div></section>;
}

function DirectHelp() {
  const whatsappLink = 'https://api.whatsapp.com/send/?phone=6281249308438&text=Halo,%20Admin%20Kasir%20WA.%20Saya%20mau%20tanya....&type=phone_number&app_absent=0';
  return <section className="border-y border-emerald-100 bg-emerald-50 px-5 py-16"><div className="mx-auto max-w-3xl text-center"><span className="text-xs font-bold uppercase tracking-widest text-[#0f6c44]">Bantuan Langsung</span><h2 className="mt-3 font-manrope text-3xl font-extrabold text-slate-900 sm:text-4xl">Ada yang ingin ditanyakan?</h2><p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">Masih bingung atau butuh bimbingan khusus untuk mengatur toko Anda? Tim kami siap membantu secara personal karena niat kami adalah memastikan usaha Anda lebih tertata dan berkembang.</p><a href={whatsappLink} target="_blank" rel="noreferrer" data-testid="landing-direct-whatsapp-help-button" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-200 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#1ebe57]"><MessageCircle size={18}/>Tanya Tim KasirWA via WhatsApp<ExternalLink size={15}/></a></div></section>;
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function Testimonials() {
  const testimonials = [
    { initials: 'HR', name: 'Hendra Raharja', role: 'Owner QuickClean Laundry Kiloan & Sepatu, Surabaya', text: 'Dulu kasir saya capek banget tiap 5 menit balas WA pelanggan tanya baju udah kering belum. Sejak pakai link tracking KasirWA, pertanyaan itu hilang 90%. Cucian juga langsung diambil karena ada pengingat otomatis.' },
    { initials: 'AW', name: 'Agung Wicaksono', role: 'Owner FixGadget Service Center, Malang', text: 'Fitur approval biaya servisnya juara! Teknisi saya tinggal foto kerusakan IC & input harga, pelanggan klik setuju dari WA. Nggak ada lagi drama pelanggan kaget dengan total biaya saat ambil handphone.' },
    { initials: 'DN', name: 'Dian Novita', role: 'Founder FocusLens Rental Kamera & iPhone, Bandung', text: 'Sistem checklist kelengkapan kamera dan foto fisik awal sangat mengamankan aset rental kami. Pengingat WA 3 jam sebelum waktu sewa habis bikin pelanggan selalu balikin kamera tepat waktu.' },
  ];
  return (
    <section id="testimoni" className="py-20 px-5 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">CERITA PEMILIK USAHA</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Apa Kata Mereka yang Sudah<br />Menggunakan KasirWA?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-yellow-400 fill-yellow-400" />)}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed flex-1 italic">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-50">
                <div className="w-10 h-10 bg-[#0f6c44] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-extrabold font-manrope">{t.initials}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const items = [
    { q: 'Apakah pelanggan saya harus download atau install aplikasi tertentu?', a: 'Tidak sama sekali. Pelanggan Anda tidak perlu download aplikasi apapun atau membuat akun baru. Mereka cukup klik link yang dikirimkan via WhatsApp, dan halaman pelacakan status pesanan akan langsung terbuka di browser HP mereka.' },
    { q: 'Apakah saya bisa menggunakan Nomor Toko saya untuk Pengiriman Pesan?', a: 'Sangat Bisa, namun Anda perlu mendaftarkan Nomor Anda terlebih dahulu.' },
    { q: 'Bagaimana jika 1.000 pesanan gratis pertama saya sudah habis?', a: 'Setelah 1.000 pesanan pertama terpakai, Anda cukup berlangganan Rp 50.000 / bulan untuk transaksi tanpa batas (unlimited), tanpa kontrak yang mengikat, dan bisa dibatalkan kapan saja.' },
    { q: 'Apakah KasirWA bisa digunakan di HP, Tablet, dan Komputer Kasir?', a: 'Ya, bisa di semua perangkat. KasirWA berbasis web app responsif. Anda bisa menggunakannya di smartphone Android/iPhone, iPad/Tablet kasir, maupun laptop dan komputer PC toko.' },
    { q: 'Apakah bisa cetak struk ke printer thermal Bluetooth?', a: 'Bisa. KasirWA mendukung cetak nota fisik dalam format thermal standar (58mm dan 80mm), format A6, maupun cetak dokumen nota A4 resmi untuk instansi/kantor.' },
  ];
  return (
    <section id="faq" className="py-20 px-5 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#0f6c44] uppercase tracking-widest">TANYA JAWAB</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-manrope mt-3">Pertanyaan yang Sering Diajukan</h2>
        </div>
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
              <button onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-50 transition-colors">
                <span className="font-semibold text-slate-800 pr-4 font-manrope text-sm sm:text-base">{item.q}</span>
                {open === i ? <ChevronUp size={18} className="text-[#0f6c44] flex-shrink-0" /> : <ChevronDown size={18} className="text-slate-400 flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="px-6 pb-5">
                  <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-12 bg-[#0f6c44] rounded-3xl p-10">
          <h3 className="text-2xl font-extrabold text-white font-manrope mb-2">Siap Mulai Pakai Gratis?</h3>
          <p className="text-emerald-200 text-sm mb-6">Bergabung dengan 500+ toko yang sudah menghemat waktu & meningkatkan omset mereka.</p>
          <button onClick={() => navigate('/register')}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0f6c44] rounded-xl font-semibold hover:bg-emerald-50 active:scale-95 transition-all">
            Daftar Gratis <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="bg-slate-900 text-slate-400 py-12 px-5">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 bg-[#0f6c44] rounded-lg flex items-center justify-center">
                <span className="text-white font-extrabold text-xs font-manrope">K</span>
              </div>
              <span className="font-extrabold text-white font-manrope">KasirWA</span>
            </div>
            <p className="text-xs max-w-xs leading-relaxed">Software POS & CRM WhatsApp No. 1 Untuk Usaha Jasa di Indonesia.</p>
          </div>
          <div className="flex flex-wrap gap-6 text-sm">
            {[['Cara Kerja', '#cara-kerja'], ['Fitur', '#fitur'], ['Testimoni', '#testimoni'], ['Harga', '#harga'], ['FAQ', '#faq']].map(([label, href]) => (
              <a key={label} href={href} className="hover:text-white transition-colors">{label}</a>
            ))}
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Masuk</button>
            <button onClick={() => navigate('/register')} className="text-[#0f6c44] font-semibold hover:text-emerald-400 transition-colors">Daftar Gratis</button>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-6 text-xs flex flex-col sm:flex-row justify-between gap-2">
          <p>© 2026 KasirWA. Hak cipta dilindungi undang-undang.</p>
          <p>Dibuat dengan semangat untuk UMKM Indonesia 🇮🇩</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <SEO
        path="/"
        title="KasirWA — Aplikasi Kasir & Notifikasi WA untuk Bisnis Jasa"
        description="Kelola laundry, rental, servis HP, MUA, dan bisnis jasa Anda dengan KasirWA. POS modern dengan notifikasi WhatsApp otomatis, laporan keuangan real-time, dan kanban board. Gratis untuk dicoba!"
        keywords="aplikasi kasir laundry, kasir rental, kasir servis hp, notifikasi whatsapp otomatis, pos bisnis jasa, kasir mua, kasir penjahit, aplikasi kasir gratis, kasirwa"
        ogType="website"
      />
      <Navbar />
      <Hero />
      <BusinessVerticals />
      <PainPoints />
      <BeforeAfter />
      <HowItWorks />
      <AppPreview />
      <CobaLangsung />
      <Features />
      <ROICalculator />
      <Pricing />
      <TutorialSection />
      <DirectHelp />
      <Testimonials />
      <FAQ />
      <Footer />
    </div>
  );
}
